// @ts-check

import {
  POPCOUNT,
  TECHNIQUE_WEIGHTS,
  digitMask,
  maskDigits,
  singleDigit,
} from "./constants.js";
import { candidateMasks, validateGivens } from "./board.js";
import {
  BOXES,
  BOX_INDEX,
  COLUMNS,
  PEERS_BY_CELL,
  ROWS,
  UNITS,
  unitDescriptor,
} from "./topology.js";

function step(technique, placements, eliminations, evidence, affectedCells) {
  return {
    technique,
    placements,
    eliminations,
    evidence,
    affected_cells: [...new Set(affectedCells)],
    weight: TECHNIQUE_WEIGHTS[technique],
    explanation_key: `hint.${technique}`,
    explanation_args: {},
  };
}

export function createHumanState(input) {
  const validated = validateGivens(input);
  if (!validated.valid) throw new Error(validated.error);
  return {
    values: Uint8Array.from(validated.board),
    candidates: candidateMasks(validated.board),
  };
}

export function findNakedSingle(state) {
  for (let cell = 0; cell < 81; cell += 1) {
    if (!state.values[cell] && POPCOUNT[state.candidates[cell]] === 1) {
      return step(
        "naked_single",
        [{ cell, digit: singleDigit(state.candidates[cell]) }],
        [],
        { cells: [cell] },
        [cell],
      );
    }
  }
  return null;
}

export function findHiddenSingle(state) {
  for (let unitIndex = 0; unitIndex < UNITS.length; unitIndex += 1) {
    const unit = UNITS[unitIndex];
    for (let digit = 1; digit <= 9; digit += 1) {
      const mask = digitMask(digit);
      const cells = unit.filter(
        (cell) => !state.values[cell] && state.candidates[cell] & mask,
      );
      if (cells.length === 1) {
        const descriptor = unitDescriptor(unitIndex);
        return step(
          "hidden_single",
          [{ cell: cells[0], digit }],
          [],
          {
            unit_type: descriptor.type,
            unit_index: descriptor.index,
            cells: unit,
            candidate_cells: cells,
          },
          cells,
        );
      }
    }
  }
  return null;
}

export function findLockedCandidate(state) {
  for (let boxIndex = 0; boxIndex < 9; boxIndex += 1) {
    const box = BOXES[boxIndex];
    for (let digit = 1; digit <= 9; digit += 1) {
      const mask = digitMask(digit);
      const cells = box.filter(
        (cell) => !state.values[cell] && state.candidates[cell] & mask,
      );
      if (cells.length < 2) continue;
      const rows = new Set(cells.map((cell) => Math.floor(cell / 9)));
      const columns = new Set(cells.map((cell) => cell % 9));
      if (rows.size === 1) {
        const row = [...rows][0];
        const eliminations = ROWS[row]
          .filter(
            (cell) =>
              BOX_INDEX[cell] !== boxIndex && state.candidates[cell] & mask,
          )
          .map((cell) => ({ cell, digit }));
        if (eliminations.length)
          return step(
            "locked_candidate",
            [],
            eliminations,
            {
              subtype: "pointing",
              unit_type: "box",
              unit_index: boxIndex,
              cells,
            },
            [...cells, ...eliminations.map((item) => item.cell)],
          );
      }
      if (columns.size === 1) {
        const column = [...columns][0];
        const eliminations = COLUMNS[column]
          .filter(
            (cell) =>
              BOX_INDEX[cell] !== boxIndex && state.candidates[cell] & mask,
          )
          .map((cell) => ({ cell, digit }));
        if (eliminations.length)
          return step(
            "locked_candidate",
            [],
            eliminations,
            {
              subtype: "pointing",
              unit_type: "box",
              unit_index: boxIndex,
              cells,
            },
            [...cells, ...eliminations.map((item) => item.cell)],
          );
      }
    }
  }
  for (let unitIndex = 0; unitIndex < 18; unitIndex += 1) {
    const unit = UNITS[unitIndex];
    for (let digit = 1; digit <= 9; digit += 1) {
      const mask = digitMask(digit);
      const cells = unit.filter(
        (cell) => !state.values[cell] && state.candidates[cell] & mask,
      );
      if (
        cells.length < 2 ||
        new Set(cells.map((cell) => BOX_INDEX[cell])).size !== 1
      )
        continue;
      const box = BOX_INDEX[cells[0]];
      const eliminations = BOXES[box]
        .filter((cell) => !unit.includes(cell) && state.candidates[cell] & mask)
        .map((cell) => ({ cell, digit }));
      if (eliminations.length)
        return step(
          "locked_candidate",
          [],
          eliminations,
          {
            subtype: "claiming",
            unit_type: unitIndex < 9 ? "row" : "column",
            unit_index: unitIndex % 9,
            cells,
          },
          [...cells, ...eliminations.map((item) => item.cell)],
        );
    }
  }
  return null;
}

function combinations(items, size, start = 0, prefix = [], output = []) {
  if (prefix.length === size) {
    output.push(prefix);
    return output;
  }
  for (
    let index = start;
    index <= items.length - (size - prefix.length);
    index += 1
  )
    combinations(items, size, index + 1, [...prefix, items[index]], output);
  return output;
}

function findNakedSubset(state, size) {
  const technique = size === 2 ? "naked_pair" : "naked_triple";
  for (let unitIndex = 0; unitIndex < UNITS.length; unitIndex += 1) {
    const unit = UNITS[unitIndex];
    const eligible = unit.filter(
      (cell) =>
        !state.values[cell] &&
        POPCOUNT[state.candidates[cell]] >= 2 &&
        POPCOUNT[state.candidates[cell]] <= size,
    );
    for (const cells of combinations(eligible, size)) {
      const union = cells.reduce(
        (mask, cell) => mask | state.candidates[cell],
        0,
      );
      if (POPCOUNT[union] !== size) continue;
      const eliminations = [];
      for (const cell of unit.filter((item) => !cells.includes(item))) {
        for (const digit of maskDigits(state.candidates[cell] & union))
          eliminations.push({ cell, digit });
      }
      if (eliminations.length) {
        const descriptor = unitDescriptor(unitIndex);
        return step(
          technique,
          [],
          eliminations,
          {
            unit_type: descriptor.type,
            unit_index: descriptor.index,
            cells,
            digits: maskDigits(union),
          },
          [...cells, ...eliminations.map((item) => item.cell)],
        );
      }
    }
  }
  return null;
}

function findHiddenSubset(state, size) {
  const technique = size === 2 ? "hidden_pair" : "hidden_triple";
  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  for (let unitIndex = 0; unitIndex < UNITS.length; unitIndex += 1) {
    const unit = UNITS[unitIndex];
    for (const subsetDigits of combinations(digits, size)) {
      const digitMasks = subsetDigits.map(digitMask);
      if (
        digitMasks.some(
          (mask) =>
            !unit.some(
              (cell) => !state.values[cell] && state.candidates[cell] & mask,
            ),
        )
      )
        continue;
      const cells = unit.filter(
        (cell) =>
          !state.values[cell] &&
          digitMasks.some((mask) => state.candidates[cell] & mask),
      );
      if (cells.length !== size) continue;
      const keepMask = digitMasks.reduce((mask, item) => mask | item, 0);
      const eliminations = cells.flatMap((cell) =>
        maskDigits(state.candidates[cell] & ~keepMask).map((digit) => ({
          cell,
          digit,
        })),
      );
      if (eliminations.length) {
        const descriptor = unitDescriptor(unitIndex);
        return step(
          technique,
          [],
          eliminations,
          {
            unit_type: descriptor.type,
            unit_index: descriptor.index,
            cells,
            digits: subsetDigits,
          },
          cells,
        );
      }
    }
  }
  return null;
}

export const findNakedPair = (state) => findNakedSubset(state, 2);
export const findNakedTriple = (state) => findNakedSubset(state, 3);
export const findHiddenPair = (state) => findHiddenSubset(state, 2);
export const findHiddenTriple = (state) => findHiddenSubset(state, 3);

export function findXWing(state) {
  for (let digit = 1; digit <= 9; digit += 1) {
    const mask = digitMask(digit);
    for (const orientation of ["row", "column"]) {
      const primary = orientation === "row" ? ROWS : COLUMNS;
      const patterns = primary
        .map((unit, index) => ({
          index,
          positions: unit
            .filter(
              (cell) => !state.values[cell] && state.candidates[cell] & mask,
            )
            .map((cell) =>
              orientation === "row" ? cell % 9 : Math.floor(cell / 9),
            ),
        }))
        .filter((item) => item.positions.length === 2);
      for (const [first, second] of combinations(patterns, 2)) {
        if (first.positions.join() !== second.positions.join()) continue;
        const eliminations = [];
        for (const position of first.positions) {
          const crossUnit =
            orientation === "row" ? COLUMNS[position] : ROWS[position];
          for (const cell of crossUnit) {
            const primaryIndex =
              orientation === "row" ? Math.floor(cell / 9) : cell % 9;
            if (
              primaryIndex !== first.index &&
              primaryIndex !== second.index &&
              state.candidates[cell] & mask
            )
              eliminations.push({ cell, digit });
          }
        }
        if (eliminations.length) {
          const corners = [first.index, second.index].flatMap((index) =>
            first.positions.map((position) =>
              orientation === "row"
                ? index * 9 + position
                : position * 9 + index,
            ),
          );
          return step(
            "x_wing",
            [],
            eliminations,
            { orientation, digit, cells: corners },
            [...corners, ...eliminations.map((item) => item.cell)],
          );
        }
      }
    }
  }
  return null;
}

export const TECHNIQUES = Object.freeze([
  findNakedSingle,
  findHiddenSingle,
  findLockedCandidate,
  findNakedPair,
  findHiddenPair,
  findNakedTriple,
  findHiddenTriple,
  findXWing,
]);

export function nextHumanStep(state) {
  for (const technique of TECHNIQUES) {
    const result = technique(state);
    if (result) return result;
  }
  return null;
}

export function applyHumanStep(state, result) {
  for (const elimination of result.eliminations)
    state.candidates[elimination.cell] &= ~digitMask(elimination.digit);
  for (const placement of result.placements) {
    state.values[placement.cell] = placement.digit;
    state.candidates[placement.cell] = 0;
    const mask = digitMask(placement.digit);
    for (const peer of PEERS_BY_CELL[placement.cell])
      state.candidates[peer] &= ~mask;
  }
}

export function solveHuman(input, maxSteps = 1000) {
  const state = createHumanState(input);
  const steps = [];
  while (state.values.some((value) => value === 0) && steps.length < maxSteps) {
    const result = nextHumanStep(state);
    if (!result) break;
    applyHumanStep(state, result);
    steps.push(result);
    if (
      state.candidates.some((mask, cell) => !state.values[cell] && mask === 0)
    )
      break;
  }
  return { solved: state.values.every(Boolean), values: state.values, steps };
}
